import { CommonModule, KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'src/app/services/app.service';
import { ReportsService } from '../reports.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { LocalizationService, LocalizedNumberFormat } from 'src/app/shared/localization/localization.service';
import { TREATED_CASES_SEARCH_RESULT_COLUMNS, ITreatedCasesReportResult, ITreatedCasesRequest,} from '../reports-models';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { IPaginatedSearch } from 'src/app/shared/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { Table, TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { DatePickerModule } from 'primeng/datepicker';


@Component({
  selector: 'app-treated-cases',
  templateUrl: './treated-cases.component.html',
  styleUrls: ['./treated-cases.component.scss'],
  imports: [CommonModule, TranslateModule, TableModule, DatePickerModule, FormsModule, CustomMultiSelectComponent]
})
export class TreatedCasesComponent implements OnInit {
  customerNumber$ = this.appService.customerNumber$;
  customers$: Observable<KeyValue<number, string>[]>;
  startDate: Date;
  endDate: Date;
  numberFormat: LocalizedNumberFormat;
  dateFormat: string;
  cols = TREATED_CASES_SEARCH_RESULT_COLUMNS;
  selectedCustomers: number[] = [];
  treatedCases: ITreatedCasesReportResult[];
  totalCases: string;
  totalNumberOfItems = 0;
  userAssignedCustomersCount: number;

  canSearch: boolean;
  showResults: boolean;
  isLoading: boolean;
  languageCode: string;

  @ViewChild('treatedCasesTable') treatedCasesTable: Table;

  private readonly unsubscribe$ = new Subject();

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private localisationService: LocalizationService,
    private notifications: MessageToUiService,
    private spinnerService: GlobalSpinnerService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.spinnerService.show();
    this.initializeCustomerList();
    this.localisationService.settings$.subscribe(
      (_) => (this.numberFormat = _.numberPipeFormat)
    );

    this.localisationService.currentLanguage$.subscribe(
      _ => this.languageCode = _
    );
    this.spinnerService.hide();
  }

  private initializeCustomerList() {
    this.customers$ = this.appService.customers$.pipe(
      tap(_ => {
        this.userAssignedCustomersCount = _.length;
        if (this.userAssignedCustomersCount == 1)
          {this.selectedCustomers = [_[0].number]; }
      }),
      map((_) => _.map((x) => ({ key: x.number, value: `${x.number} - ${x.name}` })))
    );
  }

  onGenerateReport() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    if (this.startDate == undefined) {
      this.notifications.errorKey('Errors.Reports.StartDateNotSelected');
      return false;
    }
    if (this.endDate == undefined) {
      this.notifications.errorKey('Errors.Reports.EndDateNotSelected');
      return false;
    }

    const parameters = this.getPaginatedReportParameters();
    this.isLoading = true;

    this.reportsService.getTreatedCasesReport(parameters)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }))
      .subscribe((
        casesList
      ) => {
        this.treatedCases = casesList.pageItems;
        this.totalNumberOfItems = casesList.totalCount;
        this.totalCases = this.translateService.instant('Reports.TreatedCases.TotalRows') + casesList.totalCount.toString();
      });
  }
  private getPaginatedReportParameters(): IPaginatedSearch<ITreatedCasesRequest> {
    return {
      currentPage: 0,
      pageSize: 0,
      filter: this.getReportParameters(),
    } as IPaginatedSearch<ITreatedCasesRequest>;
  }

  private getReportParameters(): ITreatedCasesRequest {
    const customerNumbers = this.selectedCustomers.join(',');
    
    return {
      customerNumbers,
      startDate: this.startDate,
      endDate: this.endDate,
    } as ITreatedCasesRequest;
  }

  public onSelectedCustomersChange(event: number[]) {
    if (event.length == 0) {
      this.treatedCases = null;
    }
  }

  onExportExcelClick() {
    if (this.selectedCustomers?.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }
    if (this.startDate == undefined) {
      this.notifications.errorKey('Errors.Reports.StartDateNotSelected');
      return false;
    }
    if (this.endDate == undefined) {
      this.notifications.errorKey('Errors.Reports.EndDateNotSelected');
      return false;
    }

    const parameters = this.getReportParameters();
    this.isLoading = true;
    this.reportsService.exportTreatedCasesForExcel(parameters, this.translateService.getDefaultLang())
      .pipe(finalize(() => { this.isLoading = false; }), takeUntil(this.unsubscribe$))
      .subscribe({
        next: blob => {
          saveAs(blob, `${this.translateService.instant('Reports.TreatedCases.Title')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`);
        },
        error: () => {
          this.notifications.genericError();
        }
    });
  }

}
